import React, { useEffect, useState } from 'react';
import { Modal } from 'react-bootstrap';
import articlesService from '../../../services/articles.service';
import { useEyeconxStore } from '../../../store/eyeconxStore';

function formatArticleDate(dateString) {
    if (!dateString) {
        return null;
    }

    const parsed = new Date(dateString);
    if (Number.isNaN(parsed.getTime())) {
        return dateString;
    }

    return parsed.toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
    });
}

function ArticleViewSkeleton() {
    return (
        <div className="article-view-modal__skeleton">
            <div className="article-view-modal__skeleton-line article-view-modal__skeleton-line--lg" />
            <div className="article-view-modal__skeleton-line" />
            <div className="article-view-modal__skeleton-line" />
            <div className="article-view-modal__skeleton-line article-view-modal__skeleton-line--md" />
            <div className="article-view-modal__skeleton-line" />
            <div className="article-view-modal__skeleton-line article-view-modal__skeleton-line--sm" />
        </div>
    );
}

export default function ArticleView({ show, articleid, articlePreview = {}, onClose }) {
    const clinicId = useEyeconxStore((state) => state.user?.clinicId);
    const [article, setArticle] = useState({});
    const [isLoading, setIsLoading] = useState(false);
    const [articleNotFound, setArticleNotFound] = useState(false);

    useEffect(() => {
        if (!show || !articleid || !clinicId) {
            return;
        }

        setArticle({});
        setIsLoading(true);
        setArticleNotFound(false);

        articlesService
            .getArticleById(clinicId, articleid)
            .then((res) => {
                setArticle(res.data);
                setIsLoading(false);
            })
            .catch(() => {
                setIsLoading(false);
                setArticleNotFound(true);
            });
    }, [articleid, clinicId, show]);

    const displayArticle = { ...articlePreview, ...article };
    const title = displayArticle.title || 'Article';
    const summary = displayArticle.summary;
    const category = displayArticle.category;
    const createdAt = formatArticleDate(displayArticle.createdAt);
    const modifiedAt = formatArticleDate(displayArticle.modifiedAt);
    const isInactive = displayArticle.status === false;

    const handlePrint = () => {
        if (articleid) {
            window.open(`/print-article/${articleid}`, '_blank');
        }
    };

    return (
        <Modal
            show={show}
            onHide={onClose}
            centered
            size="lg"
            scrollable
            backdrop="static"
            keyboard={false}
            className="article-view-modal"
            contentClassName="article-view-modal__content border-0 shadow-lg"
        >
            <Modal.Header className="article-view-modal__header border-0 pb-0">
                <div className="w-100">
                    <div className="d-flex align-items-start justify-content-between gap-3">
                        <div className="d-flex align-items-start gap-3 min-width-0">
                            <span className="article-view-modal__icon-badge flex-shrink-0">
                                <i className="bi bi-journal-richtext"></i>
                            </span>
                            <div className="min-width-0">
                                <span className="ecx-page-chip mb-2">
                                    <i className="bi bi-eye"></i>
                                    Preview
                                </span>
                                <Modal.Title as="h5" className="fw-normal text-dark mb-2 text-break">
                                    {title}
                                </Modal.Title>
                                <div className="d-flex flex-wrap align-items-center gap-2">
                                    {category && (
                                        <span className="ecx-page-chip">
                                            <i className="bi bi-folder2"></i>
                                            {category}
                                        </span>
                                    )}
                                    {modifiedAt && (
                                        <span className="article-view-modal__meta">
                                            <i className="bi bi-clock me-1"></i>
                                            Updated {modifiedAt}
                                        </span>
                                    )}
                                    {createdAt && (
                                        <span className="article-view-modal__meta">
                                            <i className="bi bi-calendar3 me-1"></i>
                                            Created {createdAt}
                                        </span>
                                    )}
                                    {isInactive && (
                                        <span className="badge bg-light text-muted">Inactive</span>
                                    )}
                                </div>
                            </div>
                        </div>
                        <button
                            type="button"
                            className="btn btn-outline-secondary rounded-pill px-3 ecx-modal__close-btn flex-shrink-0"
                            onClick={onClose}
                            aria-label="Close"
                            title="Close"
                        >
                            <i className="bi bi-x-lg"></i>
                        </button>
                    </div>
                </div>
            </Modal.Header>

            <Modal.Body className="article-view-modal__body pt-3">
                {summary && (
                    <div className="article-view-modal__summary mb-3">
                        <div className="article-view-modal__summary-label">Summary</div>
                        <p className="mb-0 text-muted">{summary}</p>
                    </div>
                )}

                <div className="article-view-modal__article-panel">
                    {isLoading ? (
                        <ArticleViewSkeleton />
                    ) : articleNotFound ? (
                        <div className="article-view-modal__empty">
                            <div className="article-view-modal__empty-icon">
                                <i className="bi bi-journal-x"></i>
                            </div>
                            <h6 className="fw-normal text-dark mb-1">Article not found</h6>
                            <p className="text-muted mb-0 small">This article may have been removed or is unavailable.</p>
                        </div>
                    ) : (
                        <div
                            className="article-view-modal__html-content"
                            dangerouslySetInnerHTML={{ __html: article.content }}
                        />
                    )}
                </div>
            </Modal.Body>

            <Modal.Footer className="article-view-modal__footer border-0 d-flex align-items-center gap-2">
                <button
                    type="button"
                    className="btn btn-outline-secondary rounded-pill px-3 ecx-modal__footer-close-btn"
                    onClick={onClose}
                >
                    <i className="bi bi-x-circle me-1"></i>
                    Close
                </button>
                <button
                    type="button"
                    className="btn btn-light-primary rounded-pill px-3"
                    onClick={handlePrint}
                    disabled={isLoading || articleNotFound}
                >
                    <i className="bi bi-printer me-1"></i>
                    Print
                </button>
            </Modal.Footer>
        </Modal>
    );
}
